import React, { useEffect, useRef, useState } from "react";
import {
  ActionButton,
  Content,
  DatePicker,
  Dialog,
  DialogTrigger,
  Flex,
  Item,
  Link,
  NumberField,
  Picker,
  ProgressCircle,
  SearchField,
  Text,
  TextField,
  View,
} from "@adobe/react-spectrum";
import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { useAsyncList } from "react-stately";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { useTranslation } from "react-i18next";
import { ListItemRequest } from "../../../../services/soap/item/requests/ListItemRequest";
import { TrashIcon, AlertIconFill } from "../../Icons/IconsLib";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { RowData, Journal, AttributeData } from "./StockChangeLineComponent";
import { useDeviceDetection } from "../../../../hooks/UseDeviceDetection";
import NumericTextField from "../../../ui/NumericTextField";

interface StockChangeLineAddEditProps {
  id?: string;
  defaultRowData: {};
  defaultJournal: Journal | null;
  selectedRowData: RowData;
  handleRowClose: () => void;
  addRecord: (record: any) => void;
  updateRecord: (record: any) => void;
  deleteRecord: (record: any) => void;
  closeDialog: (isOpen: boolean) => void;
  activeAttributesData?: AttributeData[];
  viewMode?: boolean;
  styles: any;
}

interface Character {
  name: string;
}

const StockChangeLineAddEdit: React.FC<StockChangeLineAddEditProps> = ({
  id,
  defaultRowData,
  defaultJournal,
  selectedRowData,
  handleRowClose,
  addRecord,
  updateRecord,
  deleteRecord,
  closeDialog,
  activeAttributesData,
  viewMode = false,
  styles,
}) => {
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  const { itemService, store } = useDependency();
  const [selectedRow, setSelectedRow] = useState<RowData>(
    selectedRowData || defaultRowData,
  );

  const [partNumberData, setPartNumberData] = useState<any>([]);
  const [searchMode, setSearchMode] = useState<boolean>(
    !selectedRowData?.itemName || selectedRowData.itemName.trim() === "",
  );
  const [itemNameError, setItemNameError] = useState<boolean>(false);
  const { t } = useTranslation();
  const apiDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousSelectedValueRef = useRef<any>(null);
  const [isDeviceReady, setIsDeviceReady] = useState<boolean>(false);

  useEffect(() => {
    // Set device ready after initial render to prevent flicker
    setIsDeviceReady(true);
  }, []);

  useEffect(() => {
    const hasValidPartNumber =
      selectedRowData?.itemName && selectedRowData.itemName.trim() !== "";
    setItemNameError(false);
    setSelectedRow(selectedRowData || defaultRowData);
    setSearchMode(viewMode ? false : !hasValidPartNumber);
    // In view mode, skip async list updates — fields are read-only
    if (viewMode) return;
    // If we have a valid itemRefId (from barcode scanner), set it in the list
    if (selectedRowData?.itemRefId && hasValidPartNumber) {
      list.setFilterText(selectedRowData.itemName);
      list.setSelectedKeys(new Set([selectedRowData.itemRefId]));
    } else {
      list.setFilterText(selectedRowData?.itemName || "");
    }
  }, [id, selectedRowData, defaultRowData, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (apiDebounceRef.current) {
        clearTimeout(apiDebounceRef.current);
      }
    };
  }, []);

  const validateFields = () => {
    const itemInvalid =
      !selectedRow.itemName || selectedRow.itemName.trim() === "";
    if (itemInvalid) {
      setItemNameError(true);
      setSearchMode(true);
      return false;
    }
    return true;
  };

  const validateAndSubmit = () => {
    if (!validateFields()) return;
    console.log("selectedRowData", selectedRowData);

    const action =
      selectedRowData && Number(selectedRowData.id) !== 0
        ? updateRecord
        : addRecord;
    //console.log('selectedRow', selectedRow);
    action(selectedRow);
    closeDialog(false);
  };

  const findObjectById = (id: string) => {
    // Add null/undefined check for id parameter
    if (!id || typeof id !== "string") {
      console.log(
        "findObjectById: id is null, undefined, or not a string:",
        id,
      );
      return null;
    }

    let newId = id.endsWith("_ref")
      ? id.replace("_ref", "_name")
      : id.replace("_name", "_ref");
    //console.log('id', id);

    for (const group of partNumberData) {
      const found = group.children.find(
        (child: any) => child.itemRefId === id || child.itemRefId === newId,
      );
      if (found) return found;
    }
    return null;
  };

  // Define a custom hook called "list" using the useAsyncList hook
  const list = useAsyncList<Character>({
    async load({ filterText }) {
      // Set filterText to selectedRow's partNumber if filterText is empty
      filterText =
        filterText || (selectedRow.itemName ? selectedRow.itemName : " ");
      // Update the selectedRow state with the new filterText
      setSelectedRow((prev) => ({ ...prev, partNumber: filterText }));

      // Clear any existing debounce timeout
      if (apiDebounceRef.current) {
        clearTimeout(apiDebounceRef.current);
      }

      // Debounce API calls by 200ms
      return new Promise((resolve) => {
        apiDebounceRef.current = setTimeout(async () => {
          // Concurrently fetch item data by searchName and refId using filterText
          const [itemResp, itemRespByRefID]: any = await Promise.all([
            itemService.listItem(
              new ListItemRequest(store.Server, store.SessionId, 10, 0, {
                searchName: filterText.trim(),
              }),
            ),
            itemService.listItem(
              new ListItemRequest(store.Server, store.SessionId, 10, 0, {
                refId: filterText.trim(),
              }),
            ),
          ]);

          const formatItemRefID = (ITEM: any) => ({
            id: ITEM.id,
            refId: ITEM.refId,
            itemRefId: `${ITEM.refId}_${ITEM.name}_ref`, // Custom reference ID combining refId and name
            refIdName: `${ITEM.refId}(${ITEM.name})`, // Display name combining refId and name
            itemName: ITEM.name,
            finance: ITEM.amount,
            amountWithVat: ITEM.amountWithVat, // Amount with VAT
            purchasePrice: ITEM.purchasePrice,
            statusName: ITEM.statusName,
          });

          const formatItemName = (ITEM: any) => ({
            id: ITEM.id,
            refId: ITEM.refId,
            itemRefId: ITEM.refId + "_" + ITEM.name + "_name", // Custom reference ID combining refId and name
            refIdName: `${ITEM.name}${ITEM.refId ? `(${ITEM.refId})` : ""}`, // Display name combining refId and name
            itemName: ITEM.name,
            finance: ITEM.amount,
            amountWithVat: ITEM.amountWithVat, // Amount with VAT
            purchasePrice: ITEM.purchasePrice,
            statusName: ITEM.statusName,
          });

          const options = [
            ...(itemRespByRefID.ITEM?.length
              ? [
                  {
                    name: `${itemRespByRefID.ITEM.length} ref id match`,
                    children: itemRespByRefID.ITEM.map(formatItemRefID),
                  },
                ]
              : []),
            ...(itemResp.ITEM?.length
              ? [
                  {
                    name: `${itemResp.ITEM.length} name match`,
                    children: itemResp.ITEM.map(formatItemName),
                  },
                ]
              : []),
          ];
          //console.log('options', options);
          setPartNumberData(options);
          resolve({ items: options, cursor: undefined });
        }, 200);
      });
    },
  });

  const selectedIndexChanged = (value: any) => {
    //console.log('selectedIndexChanged', value);

    // Early return if value is the same as previous value
    if (value === previousSelectedValueRef.current) {
      return;
    }

    // Add protection against null/undefined values
    if (!value) {
      console.warn("selectedIndexChanged: value is null or undefined:", value);
      return;
    }

    // Update the previous value ref
    previousSelectedValueRef.current = value;

    //console.log('partNumberData', partNumberData);'
    let selectedItem: any = findObjectById(value);
    //console.log('selectedItem', selectedItem);
    if (selectedItem) {
      setItemNameError(false);
      list.setFilterText(selectedItem.refId);
      list.setSelectedKeys(new Set([selectedItem.itemRefId]));
      setSelectedRow((prev) => ({
        ...prev,
        itemRefId: selectedItem.itemRefId,
        itemName: selectedItem.itemName,
        partNumber: selectedItem.refId,
        partID: selectedItem.id,
        finance: selectedItem.finance,
        amountWithVat: selectedItem.amountWithVat,
        purchasePrice: selectedItem.purchasePrice,
        statusName: selectedItem.statusName,
      }));
      setSearchMode(false);
    } else {
      list.setFilterText("");
      list.setSelectedKeys(new Set());
      setSelectedRow((prev) => ({ ...prev, itemRefId: "", itemName: "" }));
      setItemNameError(true);
    }
  };

  const StockChangeDialog = ({ close }: { close: () => void }) => (
    <Dialog size="S">
      <Flex UNSAFE_className={styles.delete_pop_up_parent}>
        <Content UNSAFE_className={styles.delete_pop_up_header}>
          {t("remove_stock", { ns: "layout_components" })}
        </Content>
        <View UNSAFE_className={styles.delete_pop_up_line}></View>
        <View UNSAFE_className={styles.delete_pop_up_warning_text}>
          {t("remove_stock_message", { ns: "layout_components" })}
        </View>
        <View UNSAFE_className={styles.buttons_parent}>
          <View UNSAFE_className={styles.button_cancel}>
            <Link
              UNSAFE_className={styles.delete_pop_up_cancel_text}
              isQuiet
              onPress={close}
            >
              {t("no_keep_it", { ns: "layout_components" })}
            </Link>
          </View>
          <View UNSAFE_className={styles.button_confirm}>
            <View UNSAFE_className={styles.trash_icon}>
              <TrashIcon size={32} />
            </View>
            <Link
              UNSAFE_className={styles.delete_pop_up_confirm_text}
              isQuiet
              onPress={() => deleteRecord(selectedRow)}
            >
              {t("yes_remove", { ns: "layout_components" })}
            </Link>
          </View>
        </View>
        <View UNSAFE_className={styles.ellipse_div}>
          <View UNSAFE_className={styles.delete_alert_icon}>
            <AlertIconFill size={72} />
          </View>
        </View>
      </Flex>
    </Dialog>
  );

  const RemoveStockChangeButton = () =>
    Number(selectedRowData.id) !== 0 && (
      <Flex marginTop={isMobile ? "10px" : "0"}>
        <DialogTrigger isDismissable type="modal">
          <ActionButton UNSAFE_className={styles.btn_delete}>
            {t("remove_stock", { ns: "layout_components" })}
          </ActionButton>
          {(close) => <StockChangeDialog close={close} />}
        </DialogTrigger>
      </Flex>
    );

  const StockChangeActions = () => (
    <Flex direction={{ base: "row", L: "row" }} gap={"size-200"}>
      <Flex>
        <Link
          UNSAFE_className={styles.btn_cancel}
          isQuiet
          onPress={handleRowClose}
        >
          {t("cancel", { ns: "layout_components" })}
        </Link>
      </Flex>
      <Flex>
        <Link
          width={"180px"}
          UNSAFE_className={styles.btn_confirm}
          isQuiet
          onPress={() => validateAndSubmit()}
        >
          {selectedRowData.id === "0"
            ? t("add", { ns: "layout_components" })
            : t("edit", { ns: "layout_components" })}
        </Link>
      </Flex>
    </Flex>
  );

  const StockChangeListButtons = () => {
    if (viewMode) {
      return (
        <Flex justifyContent="end" marginTop="size-100">
          <Link
            UNSAFE_className={styles.btn_cancel}
            isQuiet
            onPress={handleRowClose}
          >
            {t("close", { ns: "layout_components", defaultValue: "Close" })}
          </Link>
        </Flex>
      );
    }
    return (
      <Flex
        direction={
          deviceInfo.shouldUseMobileLayout()
            ? "column"
            : { base: "column", L: "row" }
        }
        justifyContent={"end"}
        gap={"size-100"}
        marginTop={isMobile ? "0" : "size-100"}
      >
        <Flex>
          <RemoveStockChangeButton />
        </Flex>
        <StockChangeActions />
      </Flex>
    );
  };

  // Don't render until device detection is ready to prevent layout flicker
  if (!isDeviceReady) {
    return null;
  }

  return (
    <Flex
      direction="column"
      width={"100%"}
      justifyContent={"center"}
      alignContent={"center"}
      gap={"size-200"}
      UNSAFE_style={{ overflowY: "auto" }}
    >
      {activeAttributesData && activeAttributesData.some((a) => a.name) && (
        <View
          borderWidth="thin"
          borderColor="dark"
          borderRadius="medium"
          padding="size-200"
          backgroundColor="gray-100"
        >
          <Flex direction="column" gap="size-100">
            {activeAttributesData.map((attr, index) => {
              if (!attr.name) return null;
              const attribKey = `attrib${index}` as keyof RowData;
              const attribIdKey = `attrib${index}_id` as keyof RowData;
              return (
                <Flex
                  key={index}
                  direction="row"
                  alignItems="center"
                  gap="size-150"
                >
                  <View width="size-1600">
                    <Text
                      UNSAFE_style={{
                        fontSize: "14px",
                        textAlign: "right",
                        display: "block",
                      }}
                    >
                      {attr.name}:
                    </Text>
                  </View>
                  {attr.type === 5 && attr.codebooks ? (
                    <Picker
                      aria-label={attr.name}
                      selectedKey={(selectedRow[attribIdKey] as string) || ""}
                      isDisabled={viewMode}
                      onSelectionChange={(key) => {
                        const selected = attr.codebooks?.find(
                          (c) => c.id === String(key),
                        );
                        setSelectedRow((prev) => ({
                          ...prev,
                          [attribKey]: selected?.name || "",
                          [attribIdKey]: String(key),
                        }));
                      }}
                      width="size-2400"
                    >
                      {(attr.codebooks || []).map((cb) => (
                        <Item key={cb.id}>{cb.name}</Item>
                      ))}
                    </Picker>
                  ) : (
                    <TextField
                      aria-label={attr.name}
                      value={(selectedRow[attribKey] as string) || ""}
                      isReadOnly={viewMode}
                      onChange={(v) =>
                        setSelectedRow((prev) => ({ ...prev, [attribKey]: v }))
                      }
                      width="size-2400"
                      UNSAFE_className="borderGrey"
                    />
                  )}
                </Flex>
              );
            })}
          </Flex>
        </View>
      )}
      {searchMode ? (
        <div
          className={styles.search_container}
          onKeyDownCapture={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              if (selectedRow.itemName) {
                setSearchMode(false);
              }
            }
          }}
        >
          <SearchField
            label={t("item", { ns: "layout_components", defaultValue: "Item" })}
            width={"100%"}
            value={list.filterText === " " ? "" : list.filterText}
            onChange={(v) => {
              setItemNameError(false);
              list.setFilterText(v === "" ? " " : v);
            }}
            validationState={itemNameError ? "invalid" : undefined}
            autoFocus
          />
          {list.loadingState === "loading" && (
            <Flex justifyContent="center" marginTop="size-100">
              <ProgressCircle
                isIndeterminate
                size="S"
                aria-label="Loading..."
              />
            </Flex>
          )}
          {(list.items as any[]).flatMap((g: any) => g.children || []).length >
            0 && (
            <View UNSAFE_className={styles.search_results_wrapper}>
              <table
                className={styles.search_results_table}
                cellSpacing={0}
                cellPadding={0}
              >
                <thead>
                  <tr>
                    <th>
                      {t("name", {
                        ns: "layout_components",
                        defaultValue: "Name",
                      })}
                    </th>
                    <th style={{ width: "20%" }}>
                      {t("ref_id", {
                        ns: "layout_components",
                        defaultValue: "Ref. Id",
                      })}
                    </th>
                    <th style={{ width: "20%" }}>
                      {t("product_status", {
                        ns: "layout_components",
                        defaultValue: "Product status",
                      })}
                    </th>
                    <th style={{ width: "15%" }}>
                      {t("price", {
                        ns: "layout_components",
                        defaultValue: "Price",
                      })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(list.items as any[])
                    .flatMap((g: any) => g.children || [])
                    .map((item: any) => (
                      <tr
                        key={item.itemRefId}
                        className={styles.search_result_row}
                        onClick={() => selectedIndexChanged(item.itemRefId)}
                      >
                        <td>{item.itemName}</td>
                        <td>{item.refId}</td>
                        <td>{item.statusName || ""}</td>
                        <td>{item.finance ? `${item.finance}` : ""}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </View>
          )}
        </div>
      ) : (
        <Flex direction="row" gap="size-300" alignItems="end" wrap="wrap">
          <View
            UNSAFE_style={{ flex: 2, minWidth: "240px", cursor: "pointer" }}
          >
            <TextField
              label={t("item", {
                ns: "layout_components",
                defaultValue: "Item",
              })}
              isReadOnly
              value={selectedRow.itemName || ""}
              width="100%"
              UNSAFE_className="borderGrey"
              onFocus={() => {
                if (viewMode) return;
                previousSelectedValueRef.current = null;
                list.setFilterText(" ");
                setSearchMode(true);
              }}
            />
          </View>
          <NumberField
            label={t("quantity", {
              ns: "layout_components",
              defaultValue: "Quantity",
            })}
            value={Number(selectedRow.itemQuantity) || 0}
            minValue={0}
            width="size-1200"
            isReadOnly={viewMode}
            onChange={(v) =>
              setSelectedRow((prev) => ({ ...prev, itemQuantity: v }))
            }
            UNSAFE_className="borderGrey"
          />
          <NumericTextField
            label={t("finance", {
              ns: "layout_components",
              defaultValue: "Finance",
            })}
            value={selectedRow.finance || "0"}
            width="size-1600"
            isDisabled={viewMode}
            onChange={(v) =>
              setSelectedRow((prev) => ({ ...prev, finance: v }))
            }
          />

          <DatePicker
            label={t("date", { ns: "layout_components", defaultValue: "Date" })}
            value={
              selectedRow.transactionDate &&
              selectedRow.transactionDate.length >= 10
                ? parseDate(selectedRow.transactionDate.substring(0, 10))
                : today(getLocalTimeZone())
            }
            width="size-2000"
            isReadOnly={viewMode}
            onChange={(v) =>
              setSelectedRow((prev) => ({
                ...prev,
                transactionDate: v ? v.toString() : "",
              }))
            }
            UNSAFE_className="borderGrey"
          />
        </Flex>
      )}
      <StockChangeListButtons />
    </Flex>
  );
};

export default StockChangeLineAddEdit;
